'use strict';

/**
 * T323 P4-a (LD-20) — dual-accept verification shim.
 *
 * `verifyHybridOrClassical(msg, sigObj, pubkey)` is shipped to ALL verifier call
 * sites BEFORE any signer is migrated to dual-sign. It routes on the shape of the
 * signature object:
 *
 *   sigObj.ed25519 (64-byte buffer)  → classical Ed25519 verify
 *   sigObj.mldsa65 (3309-byte buffer) → ML-DSA-65 verify
 *   both present                      → verify both; OR-accept (true if EITHER
 *                                       half passes) DURING the migration window
 *
 * Migration-window posture (IH-a, documented in P8 runbook): OR-accept is correct
 * WHILE legacy Ed25519-only tokens coexist with new dual-signed ones. It is a
 * PERMANENT downgrade path if left in place, so P8's runbook mandates tightening
 * once all signers are dual-signing AND all legacy Ed25519-only tokens have
 * expired (horizon = the longest-lived 30-day JWT). Two tightening options:
 *
 *   `{ requireBoth: true }` — all PRESENT halves must verify. An ABSENT half is
 *     NOT required (it is skipped), so a single-half signature still passes.
 *     This is a migration-window AND — it does NOT close the downgrade path
 *     (GOTCHA-896).
 *   `{ requireDual: true }` — THE IH-a DOWNGRADE GUARD. Both halves must be
 *     PRESENT (well-formed) and BOTH must verify. Stripping either half —
 *     including supplying a wrong-length half, which the shape gate skips —
 *     is REFUSED. Post-migration verifiers MUST use this option.
 *
 * `pubkey` is the dual identity public key `{ ed25519, mldsa65 }` (the classical
 * 32-byte Ed25519 key alone is also accepted for the ed25519-only branch).
 *
 * Returns a structured result; never throws for valid-shape inputs.
 */

const { ed25519 } = require('@noble/curves/ed25519.js');
const { ml_dsa65 } = require('@noble/post-quantum/ml-dsa.js');

const TEXT_ENCODER = new TextEncoder();

const ED25519_SIG_LEN = 64;
const MLDSA65_SIG_LEN = 3309;
const ED25519_PUB_LEN = 32;
const MLDSA65_PUB_LEN = 1952;

function _toBytes(msg) {
  if (msg instanceof Uint8Array) return msg;
  if (Buffer.isBuffer(msg)) return new Uint8Array(msg.buffer, msg.byteOffset, msg.byteLength);
  if (typeof msg === 'string') return TEXT_ENCODER.encode(msg);
  return null;
}

function _asU8(v) {
  if (v == null) return null;
  if (v instanceof Uint8Array) return v;
  if (Buffer.isBuffer(v)) return new Uint8Array(v);
  return null;
}

/**
 * Detailed dual-accept verify. Returns { ok, ed25519, mldsa65, mode } where
 * ed25519/mldsa65 are per-half boolean results (null = not present/checked).
 */
function verifyDetailed(msg, sigObj, pubkey, opts = {}) {
  const requireDual = !!opts.requireDual;
  const requireBoth = !!opts.requireBoth;
  const msgBytes = _toBytes(msg);
  const mode = requireDual ? 'DUAL' : (requireBoth ? 'AND' : 'OR');
  const result = { ok: false, ed25519: null, mldsa65: null, mode };
  if (!msgBytes || !sigObj || typeof sigObj !== 'object' || !pubkey) return result;

  const edPub = _asU8(pubkey.ed25519 != null ? pubkey.ed25519 : pubkey);
  const dsaPub = _asU8(pubkey.mldsa65 != null ? pubkey.mldsa65 : pubkey.mlDsa);

  const edSig = _asU8(sigObj.ed25519);
  const dsaSig = _asU8(sigObj.mldsa65);

  if (edSig && edSig.length === ED25519_SIG_LEN && edPub && edPub.length === ED25519_PUB_LEN) {
    try { result.ed25519 = ed25519.verify(edSig, msgBytes, edPub); } catch (_e) { result.ed25519 = false; }
  }
  if (dsaSig && dsaSig.length === MLDSA65_SIG_LEN && dsaPub && dsaPub.length === MLDSA65_PUB_LEN) {
    try { result.mldsa65 = ml_dsa65.verify(dsaSig, msgBytes, dsaPub); } catch (_e) { result.mldsa65 = false; }
  }

  if (requireDual) {
    // IH-a permanent-downgrade guard: BOTH halves must be PRESENT (an absent or
    // wrong-length half stays null) AND BOTH must verify. null is a REFUSAL
    // here — never filtered out of the check (GOTCHA-896).
    result.ok = result.ed25519 === true && result.mldsa65 === true;
    return result;
  }

  const checked = [result.ed25519, result.mldsa65].filter((v) => v !== null);
  if (checked.length === 0) { result.ok = false; return result; }
  result.ok = requireBoth ? checked.every((v) => v === true) : checked.some((v) => v === true);
  return result;
}

/**
 * Boolean dual-accept verify (the call-site shim). True if (OR-accept) at least
 * one present half verifies, or (requireBoth) all present halves verify, or
 * (requireDual — the IH-a downgrade guard) both halves are present AND verify.
 *
 * @param {string|Uint8Array} msg
 * @param {{ed25519?:Uint8Array, mldsa65?:Uint8Array}} sigObj
 * @param {{ed25519?:Uint8Array, mldsa65?:Uint8Array}|Uint8Array} pubkey
 * @param {{requireBoth?:boolean, requireDual?:boolean}} [opts]
 * @returns {boolean}
 */
function verifyHybridOrClassical(msg, sigObj, pubkey, opts) {
  return verifyDetailed(msg, sigObj, pubkey, opts).ok;
}

module.exports = {
  verifyHybridOrClassical,
  verifyDetailed,
  ED25519_SIG_LEN,
  MLDSA65_SIG_LEN,
};
